import i18n from "i18next"
import { initReactI18next } from "react-i18next"

export const DEFAULT_LANGUAGE = "en"
export const LANGUAGE_STORAGE_KEY = "bnb-support.language"
export const SUPPORTED_LANGUAGES = ["en", "es"] as const

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const resources = {
  en: {
    translation: {
      home: {
        noReservation: "No reservation",
        loadError: "Unable to load reservation details",
      },
      homeInfo: {
        testMode: "[Test Mode]",
        welcome: "Welcome to {{homeName}}",
      },
      legacyHome: {
        welcome: "Welcome",
      },
      loader: {
        loading: "Loading ...",
        success: "Success",
        failed: "Failed",
      },
      operation: {
        success: "Access action completed",
        integrationError: "Unable to complete the access action",
        badRequest: "Invalid request",
        notFound: "Access action not available",
        invalidReservation: "Invalid reservation",
        reservationNotFound: "Reservation not found",
        reservationNotActive: "Reservation is not active yet",
        reservationExpired: "Reservation has expired",
        permissionDenied: "You do not have permission for this action",
        defaultError: "Unable to complete the access action",
        requestFailed: "Please try again",
      },
      messages: {
        ariaLabel: "Messages",
      },
      languageSelector: {
        groupLabel: "Language selector",
        english: "Switch language to English",
        spanish: "Switch language to Spanish",
      },
      permissionButton: {
        outside_door: "Open Door",
        garage_door: "Open Gate",
        main_door: "Open Main Door",
      },
    },
  },
  es: {
    translation: {
      home: {
        noReservation: "Sin reservacion",
        loadError: "No se pudieron cargar los detalles de la reservacion",
      },
      homeInfo: {
        testMode: "[Modo de prueba]",
        welcome: "Bienvenid@ a {{homeName}}",
      },
      legacyHome: {
        welcome: "Bienvenid@",
      },
      loader: {
        loading: "Cargando ...",
        success: "Exito",
        failed: "Error",
      },
      operation: {
        success: "Accion completada",
        integrationError: "No se pudo completar la accion",
        badRequest: "Solicitud invalida",
        notFound: "Accion no disponible",
        invalidReservation: "Reservacion invalida",
        reservationNotFound: "Reservacion no encontrada",
        reservationNotActive: "La reservacion aun no esta activa",
        reservationExpired: "La reservacion ya expiro",
        permissionDenied: "No tienes permiso para esta accion",
        defaultError: "No se pudo completar la accion",
        requestFailed: "Intentalo de nuevo",
      },
      messages: {
        ariaLabel: "Mensajes",
      },
      languageSelector: {
        groupLabel: "Selector de idioma",
        english: "Cambiar idioma a ingles",
        spanish: "Cambiar idioma a espanol",
      },
      permissionButton: {
        outside_door: "Abrir puerta",
        garage_door: "Abrir porton",
        main_door: "Abrir puerta principal",
      },
    },
  },
}

const normalizeLanguage = (value?: string | null): SupportedLanguage | null => {
  if (!value) return null
  const cleanedValue = value.toLowerCase()
  if (cleanedValue.startsWith("es")) return "es"
  if (cleanedValue.startsWith("en")) return "en"
  return null
}

const getLanguageFromQuery = (): SupportedLanguage | null => {
  if (typeof window === "undefined") return null
  const params = new URLSearchParams(window.location.search)
  return normalizeLanguage(params.get("lang"))
}

const getLanguageFromStorage = (): SupportedLanguage | null => {
  if (typeof window === "undefined") return null
  return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY))
}

const getPreferredLanguage = (): SupportedLanguage => {
  return getLanguageFromQuery() || getLanguageFromStorage() || DEFAULT_LANGUAGE
}

const setDocumentLanguage = (language: string) => {
  if (typeof document === "undefined") return
  document.documentElement.lang = language
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    lng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  })
}

setDocumentLanguage(i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE)
i18n.on("languageChanged", setDocumentLanguage)

export const setPreferredLanguage = (language: SupportedLanguage) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  }

  if (i18n.language === language) {
    setDocumentLanguage(language)
    return
  }

  void i18n.changeLanguage(language)
}

export const applyPreferredLanguage = () => {
  const language = getPreferredLanguage()
  setPreferredLanguage(language)
}

export default i18n
